import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import ConfirmPassword from '../../ConfirmPassword/ConfirmPassword';
import { ReactComponent as SecurityIcon } from '../security-icon.svg';
import { Button, Col, Container, Modal, ModalFooter, Row } from 'reactstrap';
import {
    doEmailUpdate,
    doPasswordUpdate,
    doSignOut,
    reauthenticate,
    resetPassword,
} from '../../../../../firebase/api/auth';
import { updateUser } from '../../../../../firebase/api/users';
import { ConfirmationModal } from '../../../modal/ConfirmModal';
import './styles.css';
import { useAuth } from '../../../../../hooks/useAuth';

const SecuritySettings = ({ accountDetailsDTO, toggle }) => {
    const {
        currentUser: { email },
    } = useAuth();
    const [
        displayChangeDetailsConfirmationDialog,
        setDisplayChangeDetailsConfirmationDialog,
    ] = useState(false);
    const [confirmationPassword, setConfirmationPassword] = useState(null);
    const [displayChangePasswordBody, setDisplayChangePasswordBody] = useState(false);
    const [displaySendResetPasswordDialog, setDisplaySendResetPasswordDialog] = useState(false);
    const [displayUpdatePasswordInfoDialog, sedDisplayUpdatePasswordInfoDialog] = useState(false);

    const [accountSecurityDTO, setAccountSecurityDTO] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        error: null,
    });

    const reuthenticateAndUpdateUserDetails = useCallback(() => {
        const newAccountDetailsDTO = { accountDetailsDTO };
        reauthenticate(confirmationPassword).then(() => {
            doEmailUpdate(newAccountDetailsDTO.email).then(() => {
                updateUser(newAccountDetailsDTO).then(() => {
                    setDisplayChangeDetailsConfirmationDialog(false);
                    toggle();
                });
            });
        });
    }, [toggle, confirmationPassword, accountDetailsDTO]);

    const onChangePasswordClick = useCallback(() => {
        setDisplayChangePasswordBody(!displayChangePasswordBody);
    }, [displayChangePasswordBody]);

    const onPasswordChange = useCallback(
        event => {
            const newAccountSecurityDTO = { ...accountSecurityDTO };
            newAccountSecurityDTO[event.target.name] = event.target.value;
            setAccountSecurityDTO(newAccountSecurityDTO);
        },
        [accountSecurityDTO]
    );

    const reuthenticateAndUpdatePassword = useCallback(() => {
        const newAccountSecurityDTO = { ...accountSecurityDTO };

        reauthenticate(newAccountSecurityDTO.currentPassword)
            .then(() => {
                doPasswordUpdate(newAccountSecurityDTO.newPassword).then(() => {
                    sedDisplayUpdatePasswordInfoDialog(true);
                });
            })
            .catch(error => {
                newAccountSecurityDTO.error = error.message;
                setAccountSecurityDTO(newAccountSecurityDTO);
            });
    }, [accountSecurityDTO]);

    const onNewPasswordSubmit = useCallback(() => {
        //TODO: add validation for current, new, confirm passwords
        reuthenticateAndUpdatePassword();
    }, [reuthenticateAndUpdatePassword]);

    const handleResetPassword = useCallback(async () => {
        await resetPassword(email);
        doSignOut();
    }, [email]);

    return (
        <>
            {displayChangeDetailsConfirmationDialog && (
                <ConfirmPassword
                    modal={displayChangeDetailsConfirmationDialog}
                    toggle={() => {
                        setDisplayChangeDetailsConfirmationDialog(
                            !displayChangeDetailsConfirmationDialog
                        );
                    }}
                    onConfirm={reuthenticateAndUpdateUserDetails}
                    onInputChange={e => {
                        setConfirmationPassword(e.target.value);
                    }}
                />
            )}
            <div className="align-items-center d-flex security-settings__header w-100 px-3">
                <SecurityIcon />
                <span className="security-settings__header-text ml-3">
                    <b>Security</b> Details
                </span>
            </div>
            <div className="py-5 px-3">
                <div className="d-flex">
                    <div>
                        {displayChangePasswordBody}
                        <input
                            type="button"
                            value={displayChangePasswordBody ? 'Cancel' : 'Update password'}
                            className="ml-3 reset-password__button"
                            onClick={
                                displayChangePasswordBody
                                    ? onChangePasswordClick
                                    : onChangePasswordClick
                            }
                        />
                    </div>
                    <div>
                        {displayChangePasswordBody}
                        <input
                            className="ml-3 forgot-password__button"
                            type="button"
                            value="Forgot password"
                            onClick={() => {
                                setDisplaySendResetPasswordDialog(true);
                            }}
                        />
                    </div>
                </div>
                {displayChangePasswordBody && (
                    <Container className="my-3 change-pw-body" fluid>
                        <Row>
                            <Col xs={12} md={3}>
                                <div className="form-group">
                                    <label>Current Password</label>
                                    <input
                                        type="password"
                                        name="currentPassword"
                                        className="form-control"
                                        placeholder={accountSecurityDTO.currentPassword}
                                        value={accountSecurityDTO.currentPassword}
                                        onChange={onPasswordChange}
                                    />
                                </div>
                            </Col>
                            <Col xs={12} md={3}>
                                <div className="form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        className="form-control"
                                        placeholder="New Password"
                                        value={accountSecurityDTO.newPassword}
                                        onChange={onPasswordChange}
                                    />
                                </div>
                            </Col>
                            <Col xs={12} md={3}>
                                <div className="form-group">
                                    <label>Confirm Password</label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        className="form-control"
                                        placeholder="Confirm Password"
                                        value={accountSecurityDTO.confirmPassword}
                                        onChange={onPasswordChange}
                                    />
                                </div>
                            </Col>

                            <Col xs={12} md={3} className="update-pw-save">
                                {displayChangePasswordBody}
                                <Button
                                    className="ml-3 save-password__button"
                                    color="primary"
                                    onClick={
                                        displayChangePasswordBody
                                            ? onNewPasswordSubmit
                                            : onChangePasswordClick
                                    }
                                >
                                    {displayChangePasswordBody ? 'Save' : ''}
                                </Button>
                            </Col>
                        </Row>

                        {accountSecurityDTO.error && (
                            <Row>
                                <Col xs={12} md={12} className="text-right">
                                    <p>{accountSecurityDTO.error}</p>
                                </Col>
                            </Row>
                        )}
                    </Container>
                )}
                {displaySendResetPasswordDialog && (
                    <ConfirmationModal
                        headerTitle="Password resetting"
                        bodyText={`Send an email to ${accountDetailsDTO.email} with a link to reset a password`}
                        onConfirm={handleResetPassword}
                        confirmButtonText="Send"
                        onCancel={() => {
                            setDisplaySendResetPasswordDialog(false);
                        }}
                        cancelButtonText="Cancel"
                        className="reset"
                    />
                )}
                {displayUpdatePasswordInfoDialog && (
                    <ConfirmationModal
                        headerTitle="New password"
                        bodyText={`${accountDetailsDTO.firstName} your new password: ${accountSecurityDTO.newPassword}`}
                        onConfirm={() => {
                            sedDisplayUpdatePasswordInfoDialog(false);
                        }}
                        confirmButtonText="Continue"
                    />
                )}
            </div>
        </>
    );
};

SecuritySettings.propTypes = {
    accountDetailsDTO: PropTypes.object,
    toggle: PropTypes.func,
};

export default React.memo(SecuritySettings);
